// EXPLODE (X) and JOIN (J): polygons and polylines split into single segments,
// touching lines merged into polylines. docs/claude/uiModules.md.
import {
  type Polygon,
  type Vec2,
  type Wall,
} from '../../shared/types';
import { selectedObjectsOf } from '../selection';
import {
  flashToolStatus,
  layers,
  newId,
  pushHistory,
  state,
} from './core';
import {
  explodeSegments,
  joinChains,
} from '../geometry';
import { tr } from '../i18n';
import { polylineIsClosed } from '../wallTool';
import {
  deselectAll,
  freshCopies,
  LINE_DEFAULT_COLOR,
  LINE_DEFAULT_HEIGHT,
  LINE_DEFAULT_THICKNESS,
  setObjectSelection,
} from '../main';
import { removeObjectsById } from './deleteSelection';
import {
  moveVertices,
  samePlace,
} from '../anchors';

/** A wall / line stored closed (first point repeated last). */
function wallIsClosed(pts: readonly Vec2[]): boolean {
  return polylineIsClosed(pts);
}

/** What EXPLODE would split: selected polygons (not a circle/ellipse — it has no
 *  real corners) and walls / lines of more than one segment. */
export function explodable(): { polys: Polygon[]; walls: Wall[] } {
  return {
    polys: selectedObjectsOf(state, state.drawing, 'polygon').filter((p) => !p.ellipse),
    walls: selectedObjectsOf(state, state.drawing, 'wall').filter((w) => w.points.length > 2),
  };
}

/** EXPLODE (X): a polygon becomes one line per edge (in its outline colour, holes
 *  included); a line or wall of several segments becomes one piece per segment,
 *  keeping all its properties. The pieces come out selected. */
export function explodeSelection(): void {
  const { polys, walls } = explodable();
  if (!polys.length && !walls.length) { flashToolStatus(tr('Nothing to explode — select a polygon, or a line or wall of several segments')); return; }
  pushHistory();
  const uid = (): string => newId('line');
  const made: string[] = [];
  // The pieces stay on the layer their source sat on (not the current one).
  const add = (w: Wall): Wall => { (state.drawing.walls ??= []).push(w); made.push(w.id); freshCopies.add(w.id); return w; };
  const m = layers();
  const sources = [...polys, ...walls];
  // What is anchored on a source's vertices or along its sides goes onto its
  // pieces, and so do its doors / panes / openings and pinned lengths (anchors.ts).
  moveVertices(state.drawing, sources.map((o) => o.id), () => {
    const into = new Map<string, Wall[]>();
    for (const p of polys) {
      const layer = m.layerOf.get(p.id);
      [p.points, ...(p.holes ?? [])].forEach((ring, r) => {
        for (const seg of explodeSegments(ring, true)) {
          const piece = add({ id: uid(), points: seg, style: 'line', thickness: LINE_DEFAULT_THICKNESS, align: 'center', fill: p.stroke || LINE_DEFAULT_COLOR, fillOpacity: p.strokeOpacity ?? 1, height: LINE_DEFAULT_HEIGHT, ...(layer ? { layer } : {}) });
          if (r === 0) (into.get(p.id) ?? into.set(p.id, []).get(p.id)!).push(piece);
        }
      });
    }
    for (const w of walls) {
      const closed = wallIsClosed(w.points);
      for (const seg of explodeSegments(closed ? w.points.slice(0, -1) : w.points, closed)) {
        const piece = structuredClone(w);
        piece.id = uid();
        delete piece.name;
        piece.points = seg;
        (into.get(w.id) ?? into.set(w.id, []).get(w.id)!).push(add(piece));
      }
    }
    const where = new Map(sources.map((o) => [o.id, samePlace(o.points, into.get(o.id) ?? [])]));
    return (id, v) => where.get(id)?.(v) ?? null;
  });
  removeObjectsById(new Set(sources.map((o) => o.id)));
  deselectAll();
  setObjectSelection({ wall: made });
}

/** JOIN (J): the selected lines — or walls of one thickness — whose ends touch
 *  (within half a millimetre) merge into single polylines, a chain that closes on
 *  itself stored closed. Each joined line takes the first piece's properties. */
export function joinSelection(): void {
  const walls = selectedObjectsOf(state, state.drawing, 'wall');
  const groups = new Map<string, Wall[]>();
  for (const w of walls) {
    const key = w.style === 'line' ? 'line' : `band:${w.thickness}:${w.align}`;
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(w);
  }
  const made: string[] = [];
  const gone = new Set<Wall>();
  // What is anchored on a joined line's vertices or along its sides goes onto
  // the line it joined, and so do its doors / panes / openings and pinned lengths.
  moveVertices(state.drawing, walls.map((w) => w.id), () => {
    const into = new Map<string, Wall[]>();
    for (const g of groups.values()) {
      if (g.length < 2) continue;
      const chains = joinChains(g.map((w) => (wallIsClosed(w.points) ? w.points.slice(0, -1) : w.points)), 0.5);
      if (chains.length >= g.length) continue; // nothing in this group touched
      if (!made.length) pushHistory();
      const joined: Wall[] = [];
      for (const c of chains) {
        const w = structuredClone(g[c.from]);
        w.id = newId('line');
        delete w.name;
        w.points = c.points;
        (state.drawing.walls ??= []).push(w);
        made.push(w.id);
        joined.push(w);
        freshCopies.add(w.id); // on its source's layer, not the current one
      }
      for (const w of g) { gone.add(w); into.set(w.id, joined); }
    }
    if (!made.length) return null;
    const where = new Map(walls.map((w) => [w.id, into.has(w.id) ? samePlace(w.points, into.get(w.id)!, 0.5) : null]));
    return (id, v) => { const f = where.get(id); return f ? f(v) : { id, vertex: v }; };
  });
  if (!made.length) { flashToolStatus(tr('Nothing to join — select lines (or walls of one thickness) whose ends touch')); return; }
  removeObjectsById(new Set([...gone].map((w) => w.id)));
  deselectAll();
  setObjectSelection({ wall: made });
}
